import { api, apiClient } from '../../../../core/utils/apiClient';
import { authService } from '../../../auth/services/auth.service';
import {
  LeadershipEntry,
  LeadershipCreatePayload,
  LeadershipUpdatePayload,
  DemoVideoEntry,
  DemoVideoCreatePayload,
  DemoVideoUpdatePayload,
  CompanyFieldOverridePayload,
} from '../types/override.types';

const BASE = '/api/v1/companies';

export class CompanyOverrideService {
  // ── Leadership ──

  async listLeadership(companyId: number): Promise<LeadershipEntry[]> {
    try {
      const response = await api.get(`${BASE}/${companyId}/leadership`);
      if (response.ok) {
        return await response.json();
      }
      return [];
    } catch (error) {
      console.error('Error fetching leadership:', error);
      return [];
    }
  }

  async createLeadership(companyId: number, payload: LeadershipCreatePayload): Promise<LeadershipEntry | null> {
    try {
      const response = await api.post(`${BASE}/${companyId}/leadership`, payload);
      if (response.ok) {
        return await response.json();
      }
      const err = await response.json().catch(() => null);
      throw new Error(err?.detail || 'Failed to create leadership entry');
    } catch (error: any) {
      console.error('Error creating leadership:', error);
      throw error;
    }
  }

  async updateLeadership(companyId: number, leaderId: string, payload: LeadershipUpdatePayload): Promise<LeadershipEntry | null> {
    try {
      const response = await api.put(`${BASE}/${companyId}/leadership/${leaderId}`, payload);
      if (response.ok) {
        return await response.json();
      }
      const err = await response.json().catch(() => null);
      throw new Error(err?.detail || 'Failed to update leadership entry');
    } catch (error: any) {
      console.error('Error updating leadership:', error);
      throw error;
    }
  }

  async importLeadershipFromEnrichment(
    companyId: number
  ): Promise<{ imported_count: number; skipped_count: number; leaders: LeadershipEntry[] }> {
    const response = await api.post(`${BASE}/${companyId}/leadership/import-from-enrichment`, {});
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.detail || 'Failed to import leadership from enrichment');
    }
    return await response.json();
  }

  async deleteLeadership(companyId: number, leaderId: string): Promise<void> {
    try {
      const response = await api.delete(`${BASE}/${companyId}/leadership/${leaderId}`);
      if (!response.ok && response.status !== 204) {
        const err = await response.json().catch(() => null);
        throw new Error(err?.detail || 'Failed to delete leadership entry');
      }
    } catch (error: any) {
      console.error('Error deleting leadership:', error);
      throw error;
    }
  }

  async uploadHeadshot(companyId: number, leaderId: string, file: File): Promise<{ headshot_url: string; s3_key: string }> {
    const formData = new FormData();
    formData.append('file', file);

    const token = authService.getApiToken();
    const response = await apiClient(`${BASE}/${companyId}/leadership/${leaderId}/headshot`, {
      method: 'POST',
      body: formData,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.detail || 'Failed to upload headshot');
    }
    return await response.json();
  }

  async deleteHeadshot(companyId: number, leaderId: string): Promise<void> {
    const response = await api.delete(`${BASE}/${companyId}/leadership/${leaderId}/headshot`);
    if (!response.ok && response.status !== 204) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.detail || 'Failed to delete headshot');
    }
  }

  async fetchHeadshotBlob(companyId: number, leaderId: string): Promise<string | null> {
    try {
      const response = await apiClient(`${BASE}/${companyId}/leadership/${leaderId}/headshot`, {
        method: 'GET',
      });
      if (!response.ok) return null;
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch {
      return null;
    }
  }

  // ── Demo Videos ──

  async listDemoVideos(companyId: number): Promise<DemoVideoEntry[]> {
    try {
      const response = await api.get(`${BASE}/${companyId}/demo-videos`);
      if (response.ok) {
        return await response.json();
      }
      return [];
    } catch (error) {
      console.error('Error fetching demo videos:', error);
      return [];
    }
  }

  async createDemoVideo(companyId: number, payload: DemoVideoCreatePayload): Promise<DemoVideoEntry | null> {
    try {
      const response = await api.post(`${BASE}/${companyId}/demo-videos`, payload);
      if (response.ok) {
        return await response.json();
      }
      const err = await response.json().catch(() => null);
      throw new Error(err?.detail || 'Failed to create demo video');
    } catch (error: any) {
      console.error('Error creating demo video:', error);
      throw error;
    }
  }

  async updateDemoVideo(companyId: number, videoId: string, payload: DemoVideoUpdatePayload): Promise<DemoVideoEntry | null> {
    try {
      const response = await api.put(`${BASE}/${companyId}/demo-videos/${videoId}`, payload);
      if (response.ok) {
        return await response.json();
      }
      const err = await response.json().catch(() => null);
      throw new Error(err?.detail || 'Failed to update demo video');
    } catch (error: any) {
      console.error('Error updating demo video:', error);
      throw error;
    }
  }

  async deleteDemoVideo(companyId: number, videoId: string): Promise<void> {
    try {
      const response = await api.delete(`${BASE}/${companyId}/demo-videos/${videoId}`);
      if (!response.ok && response.status !== 204) {
        const err = await response.json().catch(() => null);
        throw new Error(err?.detail || 'Failed to delete demo video');
      }
    } catch (error: any) {
      console.error('Error deleting demo video:', error);
      throw error;
    }
  }

  // ── Field Overrides ──

  async overrideCompanyFields(companyId: number, payload: CompanyFieldOverridePayload): Promise<{ updated_fields: string[]; company_id: number }> {
    try {
      const response = await api.put(`${BASE}/${companyId}/override`, payload);
      if (response.ok) {
        return await response.json();
      }
      const err = await response.json().catch(() => null);
      throw new Error(err?.detail || 'Failed to override company fields');
    } catch (error: any) {
      console.error('Error overriding fields:', error);
      throw error;
    }
  }
}

export const companyOverrideService = new CompanyOverrideService();
